import { useEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../meta';
import { getContainedSize } from '../../helpers';
import { useLightbox } from '../lightbox';

const getPositionStyles = (position) => {
  switch (position) {
    case 'top-left':
      return css`
        left: 0;
        top: 0;
      `;
    case 'left':
      return css`
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      `;
    case 'bottom-left':
      return css`
        left: 0;
        bottom: 0;
      `;
    case 'top':
      return css`
        left: 50%;
        transform: translateX(-50%);
        top: 0;
      `;
    case 'bottom':
      return css`
        left: 50%;
        transform: translateX(-50%);
        bottom: 0;
      `;
    case 'top-right':
      return css`
        top: 0;
        right: 0;
      `;
    case 'right':
      return css`
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      `;
    case 'bottom-right':
      return css`
        right: 0;
        bottom: 0;
      `;
    default:
      return '';
  }
};

export function WatermarkLogo({
  imageRef,
  watermarkWrapperMaxHeight,
  imageCopyProtection,
  needWrapperSize
}) {
  const {
    watermarkType,
    watermarkText,
    watermarkPosition,
    watermarkTextSize,
    watermarkLogo,
    watermarkLogoSize
  } = useSettings();

  const [imgWidth, setImgWidth] = useState(100);
  const [wrapperWidth, setWrapperWidth] = useState(100);
  const [wrapperHeight, setWrapperHeight] = useState(100);

  const { lightboxContainerClass } = useLightbox();
  const positionClass = `${meta.stylePrefix}-item-data-watermarkPosition-${watermarkPosition.position}`;

  useEffect(() => {
    const ro = new ResizeObserver(() =>
      requestAnimationFrame(() => {
        if (!imageRef.current) {
          return;
        }

        const [imgWidth, imgHeight] = getContainedSize(imageRef.current);
        if (needWrapperSize) {
          setImgWidth(imgWidth);
        } else {
          setImgWidth(imageRef.current.clientWidth);
        }
        setWrapperWidth(imgWidth);
        setWrapperHeight(imgHeight);
      })
    );

    if (imageRef.current) {
      ro.observe(imageRef.current);
    }

    return () => {
      ro.disconnect();
    };
  });

  function checkCopyProtection(e) {
    if (imageCopyProtection) {
      e.preventDefault();
    }
  }

  return (
    <Wrapper
      $needWrapperSize={needWrapperSize}
      $watermarkWrapperMaxHeight={watermarkWrapperMaxHeight}
      $wrapperWidth={wrapperWidth}
      $wrapperHeight={wrapperHeight}
    >
      <GlobalWatermarkStyles
        $lightboxContainerClass={lightboxContainerClass}
        $position={watermarkPosition.position}
        $imgWidth={imgWidth}
        $watermarkTextSize={watermarkTextSize}
        $watermarkLogoSize={watermarkLogoSize}
      />

      <LogoContainer
        className={positionClass}
        $position={watermarkPosition.position}
        $imgWidth={imgWidth}
        $watermarkTextSize={watermarkTextSize}
        $watermarkLogoSize={watermarkLogoSize}
      >
        {watermarkType === 'text'
          ? watermarkText
          : watermarkLogo && (
              <Filter>
                <img
                  src={watermarkLogo?.url}
                  onContextMenu={checkCopyProtection}
                  alt=""
                />
              </Filter>
            )}
      </LogoContainer>
    </Wrapper>
  );
}

const logoContainerStyles = css`
  color: white;
  opacity: 0.5;
  position: absolute;
  padding: 12px;
  z-index: 100;

  font-size: ${({ $imgWidth, $watermarkTextSize }) =>
    `${8 + 0.001 * $imgWidth * $watermarkTextSize}px`};

  ${({ $position }) => getPositionStyles($position)}

  & img {
    display: block;
    width: ${({ $imgWidth, $watermarkLogoSize }) =>
      `${40 + 0.002 * $imgWidth * $watermarkLogoSize}px`};
  }
`;

const Wrapper = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-watermarkLogoWrapper`
})`
  display: block;
  z-index: 2;

  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  top: ${({ $needWrapperSize, $wrapperHeight, $watermarkWrapperMaxHeight }) =>
    $needWrapperSize
      ? `${($watermarkWrapperMaxHeight - $wrapperHeight) / 2}px`
      : '0'};

  width: ${({ $needWrapperSize, $wrapperWidth }) =>
    $needWrapperSize ? `${$wrapperWidth}px` : '100%'};

  height: ${({ $needWrapperSize, $wrapperHeight }) =>
    $needWrapperSize ? `${$wrapperHeight}px` : '100%'};

  max-height: ${({ $watermarkWrapperMaxHeight }) =>
    `${$watermarkWrapperMaxHeight}px`};
`;

const LogoContainer = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-watermarkLogo`
})`
  ${logoContainerStyles}
`;

const Filter = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-watermarkLogoFilter`
})`
  filter: grayscale(100%) brightness(150%) contrast(100%) sepia(20%);
`;

const GlobalWatermarkStyles = createGlobalStyle`
  ${({ $lightboxContainerClass }) => css`
    .${$lightboxContainerClass} .${meta.stylePrefix}-item-data-watermarkLogo {
      ${logoContainerStyles}
    }
  `}
`;
